import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  type UseModalProps,
} from "@chakra-ui/react";
import { NumberInput } from "@components/ui-kit";
import { Trans, t } from "@lingui/macro";
import {
  useReturnCreditFromExpenseMutation,
  useReturnCreditFromOurWorkMutation,
} from "@src/__generated__/graphql";
import { onError } from "@src/utils/apollo";
import { currency } from "@src/utils/formatters";
import { fieldNumberModelToInputProps } from "@src/utils/forms/inputHelpers";
import { useStore } from "@src/utils/hooks";
import { NumberModel } from "@src/utils/models";
import { observer } from "mobx-react-lite";
import type React from "react";
import { type FC, useState } from "react";
import { Form } from "./Form";

export interface ModalReturnCreditProps {
  amount: number;
  from: {
    ourWorkBudgetItemId?: string;
    expenseBudgetItemId?: string;
  };
  creditAccount: {
    id: string;
    operationId: string;
  };
  onSubmit?: () => void;
}

export const ModalReturnCredit: FC<
  React.PropsWithChildren<ModalReturnCreditProps & UseModalProps>
> = ({ amount, from, creditAccount, onSubmit, ...modalProps }) => {
  const appStore = useStore();
  const [form] = useState(
    () =>
      new Form(
        new NumberModel(
          0,
          appStore.workspaceStore.settings!.currency.denomination,
        ),
      ),
  );

  const [returnCreditFromOurWork, { loading: returnCreditFromOurWorkLoading }] =
    useReturnCreditFromOurWorkMutation({
      ...onError(),
      onCompleted(data) {
        if (data.returnCreditFromOurWorkBudgetItem) {
          onCloseWrapper();
          onSubmit?.();
          appStore.UIStore.toast({
            title: t`Credit was successfully moved`,
            status: "success",
          });
        }
      },
    });

  const [returnCreditFromExpense, { loading: returnCreditFromExpenseLoading }] =
    useReturnCreditFromExpenseMutation({
      ...onError(),
      onCompleted(data) {
        data?.returnCreditFromExpenseBudgetItem && onCloseWrapper();
        onCloseWrapper();
        onSubmit?.();
      },
    });

  const onReturnCredit = async () => {
    form.maxAmount = new NumberModel(
      amount,
      appStore.workspaceStore.settings!.currency.denomination,
    );

    const { hasError } = await form.validate();

    if (hasError) {
      return;
    }

    if (from.ourWorkBudgetItemId) {
      returnCreditFromOurWork({
        variables: {
          ...form.serialize(),
          our_work_budget_item_id: from.ourWorkBudgetItemId,
          brand_credit_account_id: creditAccount.id,
          credit_account_operation_id: creditAccount.operationId,
        },
      });
    } else if (from.expenseBudgetItemId) {
      returnCreditFromExpense({
        variables: {
          ...form.serialize(),
          expense_budget_item_id: from.expenseBudgetItemId,
          brand_credit_account_id: creditAccount.id,
          credit_account_operation_id: creditAccount.operationId,
        },
      });
    } else {
      return;
    }
  };

  const onCloseWrapper = () => {
    form.reset();
    modalProps.onClose();
  };

  return (
    <Modal
      isOpen={modalProps.isOpen}
      onClose={onCloseWrapper}
      size="2xl"
      trapFocus={false}
    >
      <ModalOverlay />

      <ModalContent>
        <ModalHeader>
          <Trans>Return credit</Trans>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <NumberInput
            currency
            id="field-amount"
            label={
              t`Maximum credit to return` +
              `: ${currency.formatByWorkspace(amount)}`
            }
            {...fieldNumberModelToInputProps(form.amount)}
          />
        </ModalBody>

        <ModalFooter>
          <Button colorScheme="grey" onClick={onCloseWrapper} variant="outline">
            <Trans>Cancel</Trans>
          </Button>
          <Button
            ml={3}
            isLoading={
              returnCreditFromOurWorkLoading || returnCreditFromExpenseLoading
            }
            onClick={onReturnCredit}
          >
            <Trans>Return</Trans>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default observer(ModalReturnCredit);
